import * as Sentry from '@sentry/react';
import { ENABLE_SENTRY, SENTRY_ENVIRONMENT, COMMIT_HASH } from 'config';
import { EXPECTED_API_ERRORS } from 'Utils/constants';

const SENSITIVE_FIELDS = ['password', 'token', 'accessToken', 'refreshToken', 'cardNumber', 'cvv', 'pin', 'secret'];

function sanitizeData(data) {
    if (!data || typeof data !== 'object') return data;

    if (Array.isArray(data)) {
        return data.map(sanitizeData);
    }

    const sanitized = {};
    for (const [key, value] of Object.entries(data)) {
        if (SENSITIVE_FIELDS.some((field) => key.toLowerCase().includes(field.toLowerCase()))) {
            sanitized[key] = '[REDACTED]';
        } else if (typeof value === 'object' && value !== null) {
            sanitized[key] = sanitizeData(value);
        } else {
            sanitized[key] = value;
        }
    }
    return sanitized;
}

function isExpectedApiError(error) {
    const description = error?.response?.data?.description;
    return description && EXPECTED_API_ERRORS.includes(description);
}

function is404Error(error) {
    return error?.response?.status === 404;
}

function isTimeoutError(error) {
    const code = error?.code;
    const message = error?.message ?? '';
    return (
        code === 'ECONNABORTED' ||
        code === 'ERR_TIMED_OUT' ||
        message.toLowerCase().includes('timeout') ||
        message.includes('ERR_TIMED_OUT')
    );
}

export function init() {
    if (ENABLE_SENTRY) {
        Sentry.init({
            dsn: 'https://1e796a367e6643e997332d5be5d560ed@o1218012.ingest.sentry.io/6360021',
            environment: SENTRY_ENVIRONMENT,
            release: COMMIT_HASH || 'dev',
            replaysSessionSampleRate: 1.0,
            replaysOnErrorSampleRate: 1.0,
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.breadcrumbsIntegration({ dom: { serializeAttribute: ['data-testid'] } }),
                Sentry.replayIntegration({
                    maskAllText: false,
                    blockAllMedia: false,
                    maskAllInputs: false,
                }),
            ],
            tracesSampleRate: 1.0,
            ignoreErrors: [
                // network errors
                'Error: Connection closed',
                'AxiosError: Network Error',
                'ChunkLoadError',
                'Loading CSS chunk',
                'Loading chunk',
                'Failed to fetch',
                'NetworkError',
                'Network error',
                'AbortError',
                'Request aborted',
                'TypeError: Load failed',
                'TypeError: Failed to fetch',
                'TypeError: NetworkError when attempting to fetch resource.',
                // apexcharts errors
                "TypeError: null is not an object (evaluating 't.parentNode.getAttribute')",
                "TypeError: Cannot read properties of undefined (reading 'style')",
                // pdf.js errors
                'InvalidPDFException',
                'NetworkError when attempting to fetch resource',
                'PasswordException: No password given',
                'Error: Cannot use the same canvas during multiple render() operations. Use different canvas or ensure previous operations were cancelled or completed.',
                'InvalidPDFException: Invalid PDF structure.',
                "Can't find variable: f", // pdf.js annotation_layer
                // performance issues (LCP)
                'Large Render Blocking Asset',
                // clarity
                "ReferenceError: Can't find variable: process",

                // worker errors
                'Worker was terminated',
                // browser extensions (message-based; stack-based filtering lives in denyUrls below)
                'extDomain is not defined', // extension injecting scripts into the page
                "Can't find variable: extDomain", // same error in Safari/WebKit
                // amplitude
                'indexedDB',
                'Expected mergedSettings.identificationToken to be a string',
            ],
            // Drop errors whose stack trace originates from a browser extension.
            denyUrls: [
                /extensions\//i,
                /^chrome:\/\//i,
                /^chrome-extension:\/\//i,
                /^moz-extension:\/\//i,
                /^safari-extension:\/\//i,
                /^safari-web-extension:\/\//i,
            ],
            beforeSend(event, hint) {
                const error = hint?.originalException;

                if (is404Error(error)) {
                    // ignore 404 errors (dashboards, module renders, etc.)
                    return null;
                }
                if (isExpectedApiError(error)) {
                    return null;
                }
                if (isTimeoutError(error)) {
                    return null;
                }

                return event;
            },
        });
    }
}

function safeStringify(data) {
    if (data === null || data === undefined) return null;
    try {
        return JSON.stringify(data);
    } catch {
        return String(data);
    }
}

function parseRequestData(data) {
    if (!data) return null;
    if (typeof data === 'string') {
        try {
            return JSON.parse(data);
        } catch {
            return data;
        }
    }
    return data;
}

function classifyTimeout(error) {
    const status = error?.response?.status;
    if (status === 408) return 'server_408';
    if (status === 504) return 'server_504';
    if (error?.code === 'ECONNABORTED') return 'axios_client';
    return 'unknown';
}

function extractApiErrorContext(error) {
    if (!error?.config) return null;

    const { config, response } = error;
    const sanitizedRequestData = sanitizeData(parseRequestData(config.data));
    const sanitizedRequestParams = sanitizeData(config.params);
    const sanitizedResponseData = response ? sanitizeData(response.data) : null;
    const duration = config._startTime ? Date.now() - config._startTime : undefined;

    return {
        api: {
            method: config.method?.toUpperCase(),
            url: config.url,
            baseURL: config.baseURL,
            fullUrl: `${config.baseURL || ''}${config.url || ''}`,
            requestData: safeStringify(sanitizedRequestData),
            requestParams: safeStringify(sanitizedRequestParams),
            errorCode: error.code,
            duration,
            isOnline: navigator.onLine,
            connectionType: navigator.connection?.effectiveType ?? 'unknown',
            timeoutType: classifyTimeout(error),
        },
        response: response
            ? {
                  status: response.status,
                  statusText: response.statusText,
                  data: safeStringify(sanitizedResponseData),
              }
            : null,
    };
}

export function trackError(error, extra = {}) {
    // 401 is handled centrally in axiosInstance (token cleared + page reload).
    if (error?.response?.status === 401) {
        return;
    }

    if (isExpectedApiError(error)) {
        return;
    }

    if (isTimeoutError(error)) {
        return;
    }

    if (ENABLE_SENTRY) {
        const apiContext = extractApiErrorContext(error);

        if (apiContext) {
            Sentry.setContext('api_request', apiContext.api);
            if (apiContext.response) {
                Sentry.setContext('api_response', apiContext.response);
            }

            Sentry.setTag('api.endpoint', apiContext.api.url);
            Sentry.setTag('api.method', apiContext.api.method);
            if (apiContext.response) {
                Sentry.setTag('api.status', apiContext.response.status);
            }
            if (apiContext.api.timeoutType && apiContext.api.timeoutType !== 'unknown') {
                Sentry.setTag('api.timeout_type', apiContext.api.timeoutType);
            }
        }

        const fingerprint = apiContext
            ? [
                  '{{ default }}',
                  apiContext.api.method,
                  apiContext.api.url,
                  String(apiContext.response?.status || 'no-response'),
              ]
            : ['{{ default }}'];

        Sentry.captureException(error, {
            extra: { ...extra, apiContext },
            fingerprint,
        });
    } else {
        console.error({ error, extra });
    }
}

export function addBreadcrumb(message, category = 'user-action', data = {}) {
    if (ENABLE_SENTRY) {
        Sentry.addBreadcrumb({
            message,
            category,
            data: sanitizeData(data),
            level: 'info',
        });
    }
}

export function setUserEmail(email) {
    if (ENABLE_SENTRY) {
        Sentry.setUser({ email });
    }
}
